import { useCallback, useEffect, useRef, useState } from "react";
import {
    LayoutDashboard,
    RefreshCw,
    TrendingUp,
    CalendarRange,
    Info,
    Clock,
    Package,
    Utensils,
    Wrench,
    Server,
    Headphones,
    MessageSquareWarning,
    ChevronLeft,
    Lock,
    DoorOpen,
    DoorClosed,
    Cctv,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";
import { fmtNumber, currencySuffix } from "@/lib/format";
import OpsBranchCharts from "@/components/ops/OpsBranchCharts";
import OpsPosStrip from "@/components/ops/OpsPosStrip";

const D = OPS.opsDashboard;
const REFRESH_MS = 60000;

const MODULE_ICONS = { Package, Utensils, Wrench, Server, Headphones, MessageSquareWarning, Cctv };

const RAG_DOT = {
    green: "bg-[color:var(--success)]",
    amber: "bg-[color:var(--brand-orange)]",
    red: "bg-[color:var(--danger)]",
};
const RAG_TEXT = {
    green: "text-[color:var(--success)]",
    amber: "text-[color:var(--brand-brown)]",
    red: "text-[color:var(--danger)]",
};
const GOOD_BADGE = "bg-[color:rgba(31,122,77,0.14)] text-[color:var(--success)]";
const BAD_BADGE = "bg-[color:rgba(220,38,38,0.10)] text-[color:var(--danger)]";
const FAINT = "text-[color:var(--ink-faint)]";
const MUTED = "text-[color:var(--ink-muted)]";

// Between two parts of a cell that carry numbers. Never «·» in Arabic: in Tajawal it is drawn
// like the Arabic-Indic zero «٠», so «٢ · ١ متأخر» reads «١٠ متأخر». The Arabic comma follows a
// word (a unit or label), never sits between two numbers (it would read as the thousands mark «٬»).
const listSep = (lang) => (lang === "ar" ? "، " : " · ");

// An honest empty cell: a dash, with the reason on hover.
function Dash({ testId, title, state }) {
    return <span data-testid={testId} data-state={state} title={title} className={`text-xs font-semibold ${FAINT}`}>—</span>;
}

// A branch with its own operations manager (2026-11-01): the server sends {"status": "hidden"}
// for its QC and CCTV cells to the network ops manager, and leaves them out of the totals.
const isHidden = (tile) => tile?.status === "hidden";
function HiddenDash({ testId, t }) {
    return <Dash testId={testId} state="hidden" title={t("ops_tile_hidden")} />;
}

function SalesTileCell({ tile, lang, t, testId }) {
    if (!tile || tile.actual_status === "no_data") {
        return (
            <div data-testid={testId} title={t("tile_no_data_reason")} className="text-xs font-semibold text-[color:var(--ink-faint)]">
                —
            </div>
        );
    }
    return (
        <div data-testid={testId} className="flex flex-col gap-0.5">
            <span className="tnum text-sm font-semibold text-[color:var(--ink)]">
                {fmtNumber(tile.actual, lang, 0)}
                {tile.actual_status === "partial" ? (
                    <span title={t("tile_partial_reason")} className="ms-1 text-[10px] font-normal text-[color:var(--ink-faint)]">
                        {t("tile_partial_mark")}
                    </span>
                ) : null}
            </span>
            {tile.actual_status === "partial" ? (
                <span className="text-[10px] text-[color:var(--ink-faint)]">{t("tile_partial_short")}</span>
            ) : tile.target_status === "no_target" ? (
                <span className="text-[10px] text-[color:var(--ink-faint)] italic">{t("budget_no_target")}</span>
            ) : tile.target_status === "partial" ? (
                <span className="text-[10px] text-[color:var(--ink-faint)]">{t("tile_targets_partial")}</span>
            ) : (
                <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${RAG_TEXT[tile.rag] || "text-[color:var(--ink-muted)]"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${RAG_DOT[tile.rag] || "bg-[color:var(--ink-faint)]"}`} />
                    <span>{tile.achievement_pct != null ? `${fmtNumber(tile.achievement_pct, lang, 0)}%` : "—"}</span>
                </span>
            )}
        </div>
    );
}

function QcTileCell({ tile, t, testId }) {
    if (isHidden(tile)) return <HiddenDash testId={testId} t={t} />;
    if (!tile || tile.status === "no_data") {
        return <span data-testid={testId} title={t("tile_no_data_reason")} className="text-xs font-semibold text-[color:var(--ink-faint)]">—</span>;
    }
    const zero = tile.count === 0;
    return (
        <Badge
            data-testid={testId}
            variant="secondary"
            className={`tnum border-0 ${zero ? GOOD_BADGE : BAD_BADGE}`}
        >
            {tile.count}
        </Badge>
    );
}

// Why a workforce cell is empty: HR's reason, never the POS one.
function wfReason(tile, t) {
    if (tile?.reason === "hr_unreachable") return t("ops_wf_unreachable");
    if (tile?.reason === "hr_unconfigured") return t("ops_hr_offline");
    return t("ops_wf_no_data");
}

function WorkforceTileCell({ tile, lang, t, testId }) {
    if (!tile || tile.status === "no_data") return <Dash testId={testId} title={wfReason(tile, t)} />;
    if (tile.status === "unmapped") {
        return (
            <span
                data-testid={testId}
                data-state="unmapped"
                title={t("ops_wf_unmapped_reason")}
                className="inline-flex rounded-md bg-[color:var(--brand-orange-soft)] px-1.5 py-0.5 text-[10px] font-semibold text-[color:var(--brand-brown)] whitespace-nowrap"
            >
                {t("ops_wf_unmapped")}
            </span>
        );
    }
    return (
        <span data-testid={testId} className="tnum text-sm font-semibold text-[color:var(--ink)]">
            <span>{fmtNumber(tile.count, lang, 0)}</span>
            {tile.partial ? (
                <span title={t("ops_wf_partial_reason")} className={`ms-1 text-[10px] font-normal ${FAINT}`}>{t("tile_partial_mark")}</span>
            ) : null}
        </span>
    );
}

function CheckinChip({ f, Icon, t, suffix }) {
    return (
        <span
            title={f && f.compliance === "early_close" ? t("checkin_early_close") : undefined}
            className={`inline-flex items-center gap-1 ${!f ? "text-[color:var(--ink-faint)]" : f.compliance === "late" ? "text-[color:var(--danger)]" : f.compliance === "early_close" ? "text-[color:var(--warning)]" : "text-[color:var(--success)]"}`}
        >
            <Icon className="h-3 w-3" />
            {f ? <><bdi dir="ltr">{f.time}</bdi>{f.manually_adjusted ? "*" : ""}</> : "—"}
            {suffix ? <span className="font-normal text-[color:var(--ink-faint)]">{suffix}</span> : null}
        </span>
    );
}

// Month-to-date late / early counts, shown only when there is something to say.
function CheckinMtdLine({ mtd, t, testId }) {
    const late = (mtd?.late_open || 0) + (mtd?.late_close || 0);
    const early = mtd?.early_close || 0;
    if (!late && !early) return null;
    return (
        <span data-testid={testId} className="text-[10px] font-normal text-[color:var(--ink-muted)]">
            {t("checkin_mtd_label")}:{" "}
            {late ? <span className="font-semibold text-[color:var(--danger)]">{late} {t("checkin_late")}</span> : null}
            {late && early ? " · " : null}
            {early ? <span className="font-semibold text-[color:var(--warning)]">{early} {t("checkin_early_close")}</span> : null}
        </span>
    );
}

// Today's open, the close (today's when typed, else the PREVIOUS day's — closes are
// typed in a day or more later), and the month so far.
function CheckinTileCell({ tile, t, testId }) {
    if (!tile) {
        return <span data-testid={testId} className="text-xs text-[color:var(--ink-faint)] italic">{t("checkin_closed_short")}</span>;
    }
    const prev = tile.prev_close;
    const today = tile.status === "closed"
        ? <span className="text-xs text-[color:var(--ink-faint)] italic">{t("checkin_closed_short")}</span>
        : tile.status === "not_recorded" && !(prev && prev.status === "recorded")
            ? <span className="text-xs text-[color:var(--ink-faint)] italic">{t("checkin_not_recorded")}</span>
            : <CheckinChip f={tile.open} Icon={DoorOpen} t={t} />;
    let close = null;
    if (tile.close) {
        close = <CheckinChip f={tile.close} Icon={DoorClosed} t={t} />;
    } else if (prev && prev.status !== "closed") {
        close = (
            <span data-testid={`${testId}-prev`} title={`${t("checkin_prev_close_label")} · ${prev.date}`}>
                <CheckinChip f={prev.close} Icon={DoorClosed} t={t} suffix={`· ${t("checkin_yesterday")}`} />
            </span>
        );
    } else if (tile.status !== "closed") {
        close = <CheckinChip f={null} Icon={DoorClosed} t={t} />;
    }
    return (
        <div data-testid={testId} className="flex flex-col items-start gap-0.5 text-[11px] font-medium">
            {today}
            {close}
            <CheckinMtdLine mtd={tile.mtd} t={t} testId={`${testId}-mtd`} />
        </div>
    );
}

function CheckinTotalCell({ tile, t, testId }) {
    if (!tile) return <span data-testid={testId} className="text-[color:var(--ink-faint)]">—</span>;
    const late = (tile.late_open || 0) + (tile.late_close || 0);
    const prevLate = tile.prev_late_close || 0;
    const prevEarly = tile.prev_early_close || 0;
    const mtdLate = (tile.mtd_late_open || 0) + (tile.mtd_late_close || 0);
    const mtdEarly = tile.mtd_early_close || 0;
    return (
        <div data-testid={testId} className="flex flex-col items-center gap-0.5 text-[11px]">
            <span className={`font-semibold ${late ? "text-[color:var(--danger)]" : "text-[color:var(--success)]"}`}>{late} {t("checkin_late")}</span>
            {tile.early_close ? <span className="font-semibold text-[color:var(--warning)]">{tile.early_close} {t("checkin_early_close")}</span> : null}
            <span className="text-[color:var(--ink-faint)]">{tile.not_recorded} {t("checkin_not_recorded")}</span>
            {tile.prev_date ? (
                <span data-testid={`${testId}-prev`} className="text-[10px] text-[color:var(--ink-muted)]" title={tile.prev_date}>
                    {t("checkin_prev_close_label")}:{" "}
                    <span className={prevLate ? "font-semibold text-[color:var(--danger)]" : ""}>{prevLate} {t("checkin_late")}</span>
                    {prevEarly ? <> · <span className="font-semibold text-[color:var(--warning)]">{prevEarly} {t("checkin_early_close")}</span></> : null}
                    {tile.prev_not_recorded ? <> · {tile.prev_not_recorded} {t("checkin_not_recorded")}</> : null}
                </span>
            ) : null}
            {mtdLate || mtdEarly ? (
                <span data-testid={`${testId}-mtd`} className="text-[10px] text-[color:var(--ink-muted)]">
                    {t("checkin_mtd_label")}:{" "}
                    <span className={mtdLate ? "font-semibold text-[color:var(--danger)]" : ""}>{mtdLate} {t("checkin_late")}</span>
                    {mtdEarly ? <> · <span className="font-semibold text-[color:var(--warning)]">{mtdEarly} {t("checkin_early_close")}</span></> : null}
                </span>
            ) : null}
        </div>
    );
}

// ---------------------------------------------------------------- module columns
// «المراقبة»: open findings (green at 0, red above), who holds them, how many are serious.
function CctvTileCell({ tile, lang, t, testId }) {
    if (isHidden(tile)) return <HiddenDash testId={testId} t={t} />;
    if (!tile || tile.status !== "data") return <Dash testId={testId} title={t("ops_tile_error")} />;
    const zero = tile.count === 0;
    return (
        <div
            data-testid={testId}
            className="flex flex-col items-center gap-0.5"
            title={`${t("ops_cctv_at_manager")}: ${fmtNumber(tile.submitted || 0, lang, 0)}${listSep(lang)}${t("ops_cctv_returned")}: ${fmtNumber(tile.returned || 0, lang, 0)}`}
        >
            <Badge variant="secondary" className={`tnum border-0 ${zero ? GOOD_BADGE : BAD_BADGE}`}>
                {fmtNumber(tile.count, lang, 0)}
            </Badge>
            {!zero ? (
                <span className={`text-[10px] whitespace-nowrap ${MUTED}`}>
                    <span>{`${fmtNumber(tile.with_ops || 0, lang, 0)} ${t("ops_cctv_with_ops")}${listSep(lang)}`}</span>
                    <span className={tile.high ? "font-semibold text-[color:var(--danger)]" : ""}>
                        {`${fmtNumber(tile.high || 0, lang, 0)} ${t("ops_cctv_high")}`}
                    </span>
                </span>
            ) : null}
        </div>
    );
}

// «مركز الاتصال»: paid revenue, then «ر.س، N طلب», on the sales 'as of' day; the month to that day
// under it. The currency sits between the two numbers so the separator never touches a digit.
function CallCenterTileCell({ tile, lang, t, testId }) {
    if (!tile || tile.status === "error") return <Dash testId={testId} title={t("ops_tile_error")} />;
    if (tile.status === "no_pos") return <Dash testId={testId} title={t("ops_cc_no_pos")} />;
    const today = tile.status === "data" ? tile.today : null;
    const mtd = tile.mtd;
    return (
        <div data-testid={testId} data-state={tile.status} className="flex flex-col items-center gap-0.5" title={t("ops_cc_paid_note")}>
            {today ? (
                <span className="whitespace-nowrap">
                    <span className="tnum text-sm font-semibold text-[color:var(--ink)]">{fmtNumber(today.revenue, lang, 0)}</span>
                    <span className={`ms-1 text-[10px] ${MUTED}`}>
                        {`${currencySuffix(lang)}${listSep(lang)}${fmtNumber(today.orders, lang, 0)} ${t("ops_cc_orders")}`}
                    </span>
                </span>
            ) : (
                <span title={t("ops_cc_no_data")} className={`text-xs font-semibold ${FAINT}`}>—</span>
            )}
            {mtd ? (
                <span
                    className={`text-[10px] whitespace-nowrap ${MUTED}`}
                    title={mtd.partial ? t("ops_cc_mtd_partial") : undefined}
                >
                    <span>
                        {`${t("ops_cc_mtd")}: ${fmtNumber(mtd.revenue, lang, 0)} ${currencySuffix(lang)}${listSep(lang)}${fmtNumber(mtd.orders, lang, 0)} ${t("ops_cc_orders")}`}
                    </span>
                    {mtd.partial ? <span className={`ms-1 ${FAINT}`}>{t("tile_partial_mark")}</span> : null}
                </span>
            ) : null}
        </div>
    );
}

const SC_TONE = {
    not_started: FAINT,
    draft: "text-[color:var(--brand-brown)]",
    submitted: "text-[color:var(--ink)]",
    approved: "text-[color:var(--success)]",
};

// A signed riyal figure: the sign and digits isolated left-to-right, the currency after them.
function SignedSar({ v, lang }) {
    const sign = v > 0 ? "+" : v < 0 ? "−" : "";
    return (
        <>
            <bdi dir="ltr" className="tnum">{sign}{fmtNumber(Math.abs(v), lang, 0)}</bdi> <span>{currencySuffix(lang)}</span>
        </>
    );
}

// an uncosted line adds nothing to a count's value: say how many, so the riyals are not read as complete
function MissingCost({ n, lang, t, testId }) {
    if (!n) return null;
    return (
        <span data-testid={`${testId}-missing-cost`} title={t("ops_stock_missing_cost_title")}
              className="text-[10px] whitespace-nowrap font-semibold text-[color:var(--warning-soft-fg)]">
            <span>{t("ops_stock_missing_cost")}:</span> <span className="tnum">{fmtNumber(n, lang, 0)}</span>
        </span>
    );
}

// «المخزون»: this period's monthly count, then yesterday's daily key-item count. Riyals
// appear only when the server sends them (the viewer may see them).
function StockTileCell({ tile, lang, t, testId }) {
    if (!tile || tile.status === "error") return <Dash testId={testId} title={t("ops_tile_error")} />;
    if (tile.status === "no_location") {
        return (
            <div data-testid={testId} data-state="no_location" title={t("ops_stock_not_counted_reason")} className="flex flex-col items-center">
                <span className={`text-xs font-semibold ${FAINT}`}>—</span>
                <span className={`text-[10px] italic ${FAINT}`}>{t("ops_stock_not_counted")}</span>
            </div>
        );
    }
    const m = tile.monthly || {};
    const d = tile.daily;
    return (
        <div data-testid={testId} data-state={m.status} className="flex flex-col items-center gap-0.5 text-[11px]">
            <span className="inline-flex items-center gap-1 whitespace-nowrap" title={`${t("ops_stock_month")} · ${m.period || ""}`}>
                <span className={`font-semibold ${SC_TONE[m.status] || FAINT}`}>{t(`ops_stock_${m.status}`)}</span>
                {m.status === "draft" ? (
                    <bdi dir="ltr" className={`tnum ${MUTED}`}>{fmtNumber(m.counted_n, lang, 0)}/{fmtNumber(m.items_n, lang, 0)}</bdi>
                ) : null}
                {m.late ? (
                    <span className="rounded bg-[color:rgba(220,38,38,0.10)] px-1 text-[10px] font-semibold text-[color:var(--danger)]">{t("ops_stock_late")}</span>
                ) : null}
            </span>
            {m.value != null ? (
                <span className={`tnum text-[10px] ${MUTED}`} title={t("ops_stock_value_title")}>{fmtNumber(m.value, lang, 0)} {currencySuffix(lang)}</span>
            ) : null}
            {m.value != null ? <MissingCost n={m.missing_cost_n} lang={lang} t={t} testId={testId} /> : null}
            {d ? (
                <span className={`text-[10px] whitespace-nowrap ${MUTED}`} title={`${t("ops_stock_daily_title")} · ${d.date}`}>
                    <span>{t("ops_stock_daily_yesterday")} </span>
                    <bdi dir="ltr" className="tnum">{fmtNumber(d.done, lang, 0)}/{fmtNumber(d.total, lang, 0)}</bdi>
                    {"flags" in d ? (
                        // the flag itself separates it from a/b: no dot beside the digits
                        <span
                            title={d.flags == null ? t("ops_stock_flags_unevaluated") : t("ops_stock_flags_mtd")}
                            className={`ms-1.5 ${d.flags ? "font-semibold text-[color:var(--danger)]" : ""}`}
                        >
                            {`⚑ ${d.flags == null ? "—" : fmtNumber(d.flags, lang, 0)}`}
                        </span>
                    ) : null}
                </span>
            ) : null}
            {d && d.value != null ? (
                <span className={`tnum text-[10px] whitespace-nowrap ${MUTED}`} title={t("ops_stock_daily_value_title")}>
                    <span>{t("ops_stock_daily_value")}:</span> <SignedSar v={d.value} lang={lang} />
                </span>
            ) : null}
        </div>
    );
}

function StockTotalCell({ tile, lang, t, testId }) {
    if (!tile || tile.status !== "data") return <Dash testId={testId} title={tile?.status === "error" ? t("ops_tile_error") : t("ops_stock_not_counted_reason")} />;
    return (
        <div data-testid={testId} className="flex flex-col items-center gap-0.5 text-[11px]" title={`${t("ops_stock_month")} · ${tile.period || ""}`}>
            <span className="whitespace-nowrap font-semibold text-[color:var(--ink)]">
                <span>{t("ops_stock_approved")} </span>
                <bdi dir="ltr" className="tnum">{fmtNumber(tile.approved, lang, 0)}/{fmtNumber(tile.locations, lang, 0)}</bdi>
            </span>
            {tile.late ? <span className="font-semibold text-[color:var(--danger)]">{fmtNumber(tile.late, lang, 0)} {t("ops_stock_late")}</span> : null}
            {"flags" in tile && tile.flags != null ? (
                <span className={`text-[10px] ${tile.flags ? "font-semibold text-[color:var(--danger)]" : MUTED}`} title={t("ops_stock_flags_mtd")}>
                    ⚑ {fmtNumber(tile.flags, lang, 0)}
                </span>
            ) : null}
            {tile.value != null ? (
                <span className={`tnum text-[10px] ${MUTED}`} title={t("ops_stock_value_title")}>{fmtNumber(tile.value, lang, 0)} {currencySuffix(lang)}</span>
            ) : null}
            {tile.value != null ? <MissingCost n={tile.missing_cost_n} lang={lang} t={t} testId={testId} /> : null}
            {tile.daily_value != null ? (
                <span className={`tnum text-[10px] whitespace-nowrap ${MUTED}`} title={t("ops_stock_daily_value_title")}>
                    <span>{t("ops_stock_daily_value")}:</span> <SignedSar v={tile.daily_value} lang={lang} />
                </span>
            ) : null}
        </div>
    );
}

function maintEmptyReason(tile, t) {
    if (!tile || tile.status === "error") return t("ops_tile_error");
    if (tile.status === "unmapped") return t("ops_maint_unmapped");
    return tile.reason === "not_configured" ? t("ops_maint_not_configured") : t("ops_maint_never");
}

// «الصيانة»: open work orders (overdue in a red chip, no dot beside the digits), awaiting branch
// confirmation under it.
// Greyed with a note when the mirror is stale; «مقترح» when the branch mapping is unconfirmed.
function MaintTileCell({ tile, lang, t, testId, total = false }) {
    if (!tile || tile.status !== "data") return <Dash testId={testId} title={maintEmptyReason(tile, t)} />;
    const stale = !!tile.stale;
    const unlinked = total ? tile.unlinked_open_work_orders || 0 : 0;
    return (
        <div
            data-testid={testId}
            data-stale={stale ? "1" : "0"}
            className={`flex flex-col items-center gap-0.5 ${stale ? "opacity-60" : ""}`}
            title={stale ? t("ops_maint_stale") : t("ops_maint_open_wo")}
        >
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <span className="tnum text-sm font-semibold text-[color:var(--ink)]">{fmtNumber(tile.open_work_orders, lang, 0)}</span>
                {tile.overdue ? (
                    <span className="rounded bg-[color:rgba(220,38,38,0.10)] px-1 text-[10px] font-semibold text-[color:var(--danger)]">
                        {`${fmtNumber(tile.overdue, lang, 0)} ${t("ops_maint_overdue")}`}
                    </span>
                ) : null}
                {!total && tile.mapping && tile.mapping !== "confirmed" ? (
                    <span title={t("ops_maint_suggested_reason")} className={`rounded bg-[color:rgba(186,217,235,0.35)] px-1 text-[9px] ${MUTED}`}>
                        {t("ops_maint_suggested")}
                    </span>
                ) : null}
            </span>
            <span className={`text-[10px] whitespace-nowrap ${MUTED}`}>
                {t("ops_maint_awaiting_branch")} {fmtNumber(tile.awaiting_branch_confirmation || 0, lang, 0)}
            </span>
            {unlinked ? (
                <span className={`text-[10px] whitespace-nowrap ${FAINT}`}>{fmtNumber(unlinked, lang, 0)} {t("ops_maint_unlinked")}</span>
            ) : null}
            {stale ? <span className={`text-[10px] italic ${FAINT}`}>{t("ops_maint_stale_short")}</span> : null}
        </div>
    );
}

// The network backlog: the same cell, plus what no branch row carries.
function MaintTotalCell(props) {
    return <MaintTileCell {...props} total />;
}

// A live module column draws its own cell by the tile's `metric`; anything else is «قريباً».
const LIVE_CELLS = {
    cctv: { row: CctvTileCell, total: CctvTileCell },
    call_center: { row: CallCenterTileCell, total: CallCenterTileCell },
    stock: { row: StockTileCell, total: StockTotalCell },
    maintenance: { row: MaintTileCell, total: MaintTotalCell },
};
const liveCell = (m) => (m.live && m.metric && LIVE_CELLS[m.metric]) || null;

function ComingSoonChip({ m, t }) {
    const Icon = MODULE_ICONS[m.icon] || Lock;
    return (
        <span className="inline-flex items-center gap-1 rounded-md bg-[color:rgba(186,217,235,0.28)] px-1.5 py-0.5 text-[10px] text-[color:var(--ink-faint)]">
            <Icon className="h-3 w-3" />
            <span>{t("coming_soon")}</span>
        </span>
    );
}

function DrillDialog({ branch, meta, liveTiles, lang, t, onClose }) {
    if (!branch) return null;
    const name = lang === "ar" ? branch.branch_name_ar : branch.branch_name_en || branch.branch_name_ar;
    const stat = [
        { key: "sales_today", label: t("ops_tile_sales_today"), tile: branch.sales_today, kind: "sales" },
        { key: "sales_mtd", label: t("ops_tile_sales_mtd"), tile: branch.sales_mtd, kind: "sales" },
        { key: "qc_open", label: t("ops_tile_qc_open"), tile: branch.qc_open, kind: "qc" },
        { key: "workforce", label: t("ops_tile_workforce"), tile: branch.workforce, kind: "wf" },
    ];
    return (
        <Dialog open={!!branch} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent data-testid={D.drill} className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-[color:var(--brand-orange)]" />
                        <span>{name}</span>
                    </DialogTitle>
                    <DialogDescription>
                        {t("ops_drill_subtitle")} · <bdi dir="ltr">{meta?.business_date || "—"}</bdi>
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    {stat.map((s) => (
                        <div key={s.key} className="rounded-xl surface-2 border border-[color:var(--border-soft)] p-3">
                            <p className="text-xs text-[color:var(--ink-muted)] mb-1">{s.label}</p>
                            {s.kind === "sales" ? (
                                s.tile?.actual_status === "no_data" ? (
                                    <p className="text-xs text-[color:var(--ink-faint)]">{t("tile_no_data_reason")}</p>
                                ) : (
                                    <>
                                        <p className="tnum text-lg font-bold text-[color:var(--ink)]">{fmtNumber(s.tile.actual, lang, 0)}</p>
                                        {s.tile.target_status === "no_target" ? (
                                            <p className="text-xs text-[color:var(--ink-faint)] italic">{t("budget_no_target")}</p>
                                        ) : (
                                            <p className={`text-xs font-medium ${RAG_TEXT[s.tile.rag]}`}>
                                                <span>{t("budget_col_target")}:</span> <bdi dir="ltr">{fmtNumber(s.tile.target, lang, 0)}</bdi>
                                                <span>{`${listSep(lang)}${t(`budget_rag_${s.tile.rag}`)}`}</span>
                                            </p>
                                        )}
                                    </>
                                )
                            ) : s.kind === "qc" ? (
                                isHidden(s.tile) ? (
                                    <p data-testid={`${D.drill}-qc_open`} data-state="hidden" className="text-xs text-[color:var(--ink-faint)]">
                                        <span>{t("ops_tile_hidden")}</span>
                                    </p>
                                ) : s.tile?.status === "no_data" ? (
                                    <p className="text-xs text-[color:var(--ink-faint)]">{t("tile_no_data_reason")}</p>
                                ) : (
                                    <p className="tnum text-lg font-bold text-[color:var(--ink)]">{s.tile.count}</p>
                                )
                            ) : (
                                s.tile?.status === "no_data" ? (
                                    <p className="text-xs text-[color:var(--ink-faint)]">{wfReason(s.tile, t)}</p>
                                ) : s.tile?.status === "unmapped" ? (
                                    <p className="text-xs font-semibold text-[color:var(--brand-brown)]" title={t("ops_wf_unmapped_reason")}>{t("ops_wf_unmapped")}</p>
                                ) : (
                                    <p className="tnum text-lg font-bold text-[color:var(--ink)]">{s.tile.count}</p>
                                )
                            )}
                        </div>
                    ))}
                    {liveTiles.map((m) => {
                        const Cell = liveCell(m).row;
                        return (
                            <div key={m.key} className="rounded-xl surface-2 border border-[color:var(--border-soft)] p-3">
                                <p className="text-xs text-[color:var(--ink-muted)] mb-1">{lang === "ar" ? m.label_ar : m.label_en}</p>
                                <div className="flex justify-start">
                                    <Cell tile={branch[m.metric]} lang={lang} t={t} testId={`${D.drill}-${m.metric}`} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function OpsDashboardPage() {
    const { t, lang, tn } = useI18n();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [drill, setDrill] = useState(null);
    const timer = useRef(null);

    const load = useCallback(async (silent) => {
        if (!silent) setBusy(true);
        try {
            const r = await api.get("/ops-dashboard");
            setData(r.data);
            setError(null);
            setLastUpdated(new Date());
        } catch (e) {
            setError(e?.response?.data?.detail || t("error_generic"));
        } finally {
            if (!silent) setBusy(false);
        }
    }, [t]);

    useEffect(() => {
        load(false);
        timer.current = setInterval(() => load(true), REFRESH_MS);
        return () => clearInterval(timer.current);
    }, [load]);

    const moduleTiles = data?.module_tiles || [];
    const liveTiles = moduleTiles.filter((m) => liveCell(m));
    const total = data?.total;

    return (
        <div data-testid={D.page} className="space-y-4">
            {/* header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--brand-orange-soft)]">
                        <LayoutDashboard className="h-5 w-5 text-[color:var(--brand-orange)]" />
                    </span>
                    <div>
                        <h1 className="text-lg font-bold text-[color:var(--ink)]">{t("ops_dashboard_title")}</h1>
                        <p className="text-xs text-[color:var(--ink-muted)]">{t("ops_dashboard_subtitle")}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {lastUpdated ? (
                        <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-[color:var(--ink-faint)]">
                            <Clock className="h-3 w-3" />
                            <span>{t("ops_last_updated")}</span> <bdi dir="ltr">{lastUpdated.toLocaleTimeString(lang === "ar" ? "ar-SA" : "en-US")}</bdi>
                        </span>
                    ) : null}
                    <Button data-testid={D.refresh} variant="soft" size="sm" onClick={() => load(false)} disabled={busy} className="gap-2">
                        <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                    </Button>
                </div>
            </div>

            {/* as-of + compared-against */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--ink-muted)]">
                <span data-testid={D.asOf} className="inline-flex items-center gap-1.5 rounded-lg surface-2 border border-[color:var(--border-soft)] px-2.5 py-1">
                    <CalendarRange className="h-3.5 w-3.5 text-[color:var(--brand-sky)]" />
                    <span>{t("ops_as_of")}:</span> <bdi dir="ltr" className="font-semibold text-[color:var(--ink)]">{data?.business_date || "—"}</bdi>
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-lg surface-2 border border-[color:var(--border-soft)] px-2.5 py-1">
                    <Info className="h-3.5 w-3.5 text-[color:var(--brand-sky)]" />
                    <span>{t("budget_compared_against")}</span>{" "}
                    <span className="font-semibold text-[color:var(--ink)]">
                        {data?.compared_against ? (lang === "ar" ? data.compared_against.ar : data.compared_against.en) : t("budget_revenue_gross")}
                    </span>
                </span>
                {data && !data.hr_configured ? (
                    <span className="inline-flex items-center gap-1.5 text-[color:var(--ink-faint)]">
                        <Info className="h-3.5 w-3.5" /> <span>{t("ops_hr_offline")}</span>
                    </span>
                ) : null}
            </div>

            {error ? (
                <div className="rounded-lg border border-[color:var(--danger)] bg-[color:rgba(220,38,38,0.06)] p-3 text-sm text-[color:var(--danger)]">{error}</div>
            ) : null}

            <OpsPosStrip businessDate={data?.business_date} />

            {data?.month ? <OpsBranchCharts month={data.month} /> : null}

            <Card className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
                <CardContent className="p-0">
                    {data === null ? (
                        <div className="p-4"><Skeleton className="h-72 w-full" /></div>
                    ) : (
                        <div className="overflow-x-auto scrollbar-thin">
                            <table data-testid={D.grid} className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="border-b border-[color:var(--separator)] text-[11px] uppercase tracking-wide text-[color:var(--ink-faint)]">
                                        <th className="px-3 py-2.5 text-start font-semibold whitespace-nowrap sticky start-0 bg-[color:var(--bg-surface)] z-10">{t("budget_col_branch")}</th>
                                        <th className="px-3 py-2.5 text-start font-semibold whitespace-nowrap">{t("ops_tile_sales_today")}</th>
                                        <th className="px-3 py-2.5 text-start font-semibold whitespace-nowrap">{t("ops_tile_sales_mtd")}</th>
                                        <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap">{t("ops_tile_qc_open")}</th>
                                        <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap">{t("ops_tile_workforce")}</th>
                                        <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap">{t("ops_tile_checkin")}</th>
                                        {moduleTiles.map((m) => (
                                            <th
                                                key={m.key}
                                                data-live={liveCell(m) ? "1" : "0"}
                                                className={`px-3 py-2.5 text-center font-semibold whitespace-nowrap ${liveCell(m) ? "" : "text-[color:var(--ink-faint)]"}`}
                                            >
                                                {lang === "ar" ? m.label_ar : m.label_en}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Company total header row */}
                                    {total ? (
                                        <tr data-testid={D.totalRow} className="border-b border-[color:var(--separator)] bg-[color:rgba(224,222,217,0.55)] font-semibold">
                                            <td className="px-3 py-3 whitespace-nowrap sticky start-0 bg-[color:rgba(233,231,226,0.98)] z-10 text-[color:var(--ink)]">{t("budget_company_total")}</td>
                                            <td className="px-3 py-3"><SalesTileCell tile={total.sales_today} lang={lang} t={t} testId={D.tile("total", "sales_today")} /></td>
                                            <td className="px-3 py-3"><SalesTileCell tile={total.sales_mtd} lang={lang} t={t} testId={D.tile("total", "sales_mtd")} /></td>
                                            <td className="px-3 py-3 text-center"><QcTileCell tile={total.qc_open} t={t} testId={D.tile("total", "qc_open")} /></td>
                                            <td className="px-3 py-3 text-center"><WorkforceTileCell tile={total.workforce} lang={lang} t={t} testId={D.tile("total", "workforce")} /></td>
                                            <td className="px-3 py-3 text-center"><CheckinTotalCell tile={total.checkin} t={t} testId={D.tile("total", "checkin")} /></td>
                                            {moduleTiles.map((m) => {
                                                const cell = liveCell(m);
                                                if (!cell) return <td key={m.key} className="px-3 py-3 text-center text-[color:var(--ink-faint)]">—</td>;
                                                const Cell = cell.total;
                                                return (
                                                    <td key={m.key} className="px-3 py-3 text-center">
                                                        <div className="flex justify-center">
                                                            <Cell tile={total[m.metric]} lang={lang} t={t} testId={D.tile("total", m.metric)} />
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ) : null}

                                    {data.rows.map((r) => {
                                        return (
                                            <tr
                                                key={r.branch_id}
                                                data-testid={D.row(r.branch_id)}
                                                onClick={() => setDrill(r)}
                                                className="border-b border-[color:var(--separator)] cursor-pointer transition-colors hover:bg-[color:var(--brand-orange-soft)]"
                                            >
                                                <td className="px-3 py-3 whitespace-nowrap sticky start-0 bg-[color:var(--bg-surface)] z-10">
                                                    <span className="inline-flex items-center gap-1.5 font-medium text-[color:var(--ink)]">
                                                        <span>{tn({ name_ar: r.branch_name_ar, name_en: r.branch_name_en })}</span>
                                                        <ChevronLeft className="h-3.5 w-3.5 text-[color:var(--ink-faint)] rtl:rotate-0 ltr:rotate-180" />
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3"><SalesTileCell tile={r.sales_today} lang={lang} t={t} testId={D.tile(r.branch_id, "sales_today")} /></td>
                                                <td className="px-3 py-3"><SalesTileCell tile={r.sales_mtd} lang={lang} t={t} testId={D.tile(r.branch_id, "sales_mtd")} /></td>
                                                <td className="px-3 py-3 text-center"><QcTileCell tile={r.qc_open} t={t} testId={D.tile(r.branch_id, "qc_open")} /></td>
                                                <td className="px-3 py-3 text-center"><WorkforceTileCell tile={r.workforce} lang={lang} t={t} testId={D.tile(r.branch_id, "workforce")} /></td>
                                                <td className="px-3 py-3 text-center"><CheckinTileCell tile={r.checkin} t={t} testId={D.tile(r.branch_id, "checkin")} /></td>
                                                {moduleTiles.map((m) => {
                                                    const cell = liveCell(m);
                                                    if (!cell) {
                                                        return (
                                                            <td key={m.key} className="px-3 py-3 text-center" data-testid={D.moduleTile(r.branch_id, m.key)}>
                                                                <ComingSoonChip m={m} t={t} />
                                                            </td>
                                                        );
                                                    }
                                                    const Cell = cell.row;
                                                    return (
                                                        <td key={m.key} className="px-3 py-3 text-center">
                                                            <div className="flex justify-center">
                                                                <Cell tile={r[m.metric]} lang={lang} t={t} testId={D.tile(r.branch_id, m.metric)} />
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <DrillDialog branch={drill} meta={data} liveTiles={liveTiles} lang={lang} t={t} onClose={() => setDrill(null)} />
        </div>
    );
}
